import {
  buildRouteGpx,
  decodePolyline,
  formatDistanceMeters,
  formatDurationSeconds,
  formatRideDate,
  gpxFileName,
  type RouteSource,
  type RouteVisibility,
} from '@motogo/shared';
import type { Feature, FeatureCollection } from 'geojson';
import {
  ArrowLeft,
  Check,
  Copy,
  Download,
  Link2,
  Map as MapIcon,
  Pencil,
  Radio,
  Trash2,
  UserMinus,
  UserPlus,
  Waypoints,
  X,
} from 'lucide-react';
import { useEffect, useState, type FC } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import RideSparkline from './RideSparkline';

import { useAuth } from '@/hooks/useAuth';
import { useRoutePlan, type PlannerPrefill } from '@/hooks/useRoutePlan';
import { useSavedRouteDetail } from '@/hooks/useSavedRouteDetail';
import { useVoice } from '@/hooks/useVoice';
import { downloadGpx } from '@/lib/gpxExport';
import { deleteLocalRoute, putLocalRoute } from '@/lib/routeStore';
import { deleteRoute, followRoute, saveRoute, unfollowRoute, updateRouteMeta } from '@/lib/savedRoutes';

const SOURCE_LABELS: Record<RouteSource, string> = {
  planner: 'Planned',
  gpx: 'GPX import',
  ride: 'From ride',
  editor: 'Custom',
};

/**
 * One saved route: stats + actions. Owners manage visibility, share (link copy /
 * push-to-channel), rename, delete; non-owners follow (library-by-reference) or save a
 * copy. This page is the share-link target (/routes/:id) — guests can view, actions
 * prompt sign-in. "Show on map" feeds the stored geometry through the same plannedRoute
 * path GPX loading uses, so MapView needs no changes.
 */
const RouteDetailPage: FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { state, refresh } = useSavedRouteDetail(id);
  const { setPlannedRoute, activeRoute, setActiveRoute, setLoadedRoute } = useRoutePlan();
  const { state: authState, exitGuestMode } = useAuth();
  const { status: voiceStatus, shareRoute } = useVoice();

  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmChannelShare, setConfirmChannelShare] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [channelShared, setChannelShared] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // Two-tap confirms auto-disarm after 4s so a stray "armed" state can't linger and turn a
  // later single tap into an unintended delete / share. The cleanup also makes these safe
  // if the page unmounts (navigates) while armed.
  useEffect(() => {
    if (!confirmDelete) return;
    const t = setTimeout(() => setConfirmDelete(false), 4000);
    return () => clearTimeout(t);
  }, [confirmDelete]);
  useEffect(() => {
    if (!confirmChannelShare) return;
    const t = setTimeout(() => setConfirmChannelShare(false), 4000);
    return () => clearTimeout(t);
  }, [confirmChannelShare]);
  // Transient "copied / shared" flashes — effect-driven so the timer is cleared on unmount
  // (navigating away mid-flash) instead of firing setState on an unmounted component.
  useEffect(() => {
    if (!linkCopied) return;
    const t = setTimeout(() => setLinkCopied(false), 2500);
    return () => clearTimeout(t);
  }, [linkCopied]);
  useEffect(() => {
    if (!channelShared) return;
    const t = setTimeout(() => setChannelShared(false), 2500);
    return () => clearTimeout(t);
  }, [channelShared]);

  const route = state.status === 'ready' ? state.route : null;
  const owned = route ? (route.owned ?? true) : false;
  const frozen = route ? (route.isFrozen ?? false) : false;
  const following = route ? (route.following ?? false) : false;
  const isGuest = authState.status === 'guest';
  const inChannel = voiceStatus === 'connected';

  const routeCoordinates = (): [number, number][] =>
    route ? decodePolyline(route.polyline).map(([lat, lon]) => [lon, lat] as [number, number]) : [];

  const showOnMap = () => {
    if (!route) return;
    const features: Feature[] = [
      { type: 'Feature', geometry: { type: 'LineString', coordinates: routeCoordinates() }, properties: {} },
    ];
    const geojson: FeatureCollection = { type: 'FeatureCollection', features };
    setPlannedRoute(geojson);
    // Remember it as the "current route" so the voice tab can push it to the channel —
    // and drop any stale unsaved-route snapshot describing the previous map contents.
    setActiveRoute({ id: route.id, name: route.name, description: route.description, visibility: route.visibility });
    setLoadedRoute(null);
    navigate('/');
  };

  const exportGpx = () => {
    if (!route) return;
    const waypoints = route.plan?.kind === 'waypoints' ? route.plan.waypoints : undefined;
    downloadGpx(gpxFileName(route.name), buildRouteGpx(route.name, routeCoordinates(), waypoints));
  };

  /** Edit: waypoints plans (and plan-less GPX/ride routes, which hydrate as a track)
   *  open the M3 editor; roundTrip plans reopen the planner pre-filled — the editor
   *  can't reproduce round_trip. */
  const editRoute = () => {
    if (!route) return;
    if (route.plan?.kind !== 'roundTrip') {
      navigate(`/routes/${route.id}/edit`);
      return;
    }
    const prefill: PlannerPrefill = {
      editRouteId: route.id,
      name: route.name,
      description: route.description,
      mode: route.plan.mode,
      gravel: route.plan.gravel,
      distanceKm: route.plan.distanceKm,
      headingDegrees: route.plan.headingDegrees,
    };
    navigate('/route', { state: { prefill } });
  };

  const beginEdit = () => {
    if (!route) return;
    setEditName(route.name);
    setEditDescription(route.description ?? '');
    setActionError(null);
    setEditing(true);
  };

  const commitEdit = async () => {
    if (!route || busy) return;
    const name = editName.trim();
    if (!name) return;
    setBusy(true);
    setActionError(null);
    try {
      const description = editDescription.trim() || null;
      const updated = await updateRouteMeta(route.id, name, description);
      if (updated) {
        await putLocalRoute(updated, true).catch(() => undefined);
      } else {
        // Not on the server (unsynced local save) — rename the mirror copy; the sync
        // pass uploads the new name with the route itself.
        await putLocalRoute({ ...route, name, description }, false).catch(() => undefined);
      }
      setEditing(false);
      refresh();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Rename failed');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!route || busy) return;
    if (!confirmDelete) {
      setConfirmChannelShare(false); // arming one confirm disarms the other
      setConfirmDelete(true);
      return;
    }
    setBusy(true);
    setActionError(null);
    try {
      await deleteRoute(route.id).catch(() => false);
      await deleteLocalRoute(route.id).catch(() => undefined);
      navigate('/routes');
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Delete failed');
      setBusy(false);
    }
  };

  const setVisibility = async (visibility: RouteVisibility) => {
    if (!route || busy || visibility === route.visibility) return;
    setConfirmDelete(false); // an unrelated action disarms any pending confirm
    setConfirmChannelShare(false);
    setBusy(true);
    setActionError(null);
    try {
      const updated = await updateRouteMeta(route.id, route.name, route.description, visibility);
      if (updated) await putLocalRoute(updated, true).catch(() => undefined);
      // Keep the voice-tab share snapshot in step so it never re-bumps a now-public route down to link.
      if (activeRoute?.id === route.id) setActiveRoute({ ...activeRoute, visibility });
      refresh();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Visibility change failed');
    } finally {
      setBusy(false);
    }
  };

  const copyShareLink = async () => {
    if (!route) return;
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/routes/${route.id}`);
      setLinkCopied(true); // auto-clears via effect
    } catch {
      setActionError('Could not copy the link.');
    }
  };

  const sendToChannel = async () => {
    if (!route || busy) return;
    // Sharing a private route makes it link-visible — receivers fetch by id and a
    // private route would just 404 for them. Two-tap confirm before the bump.
    if (route.visibility === 'private' && !confirmChannelShare) {
      setConfirmDelete(false); // arming one confirm disarms the other
      setConfirmChannelShare(true);
      return;
    }
    setBusy(true);
    setActionError(null);
    setConfirmChannelShare(false);
    try {
      if (route.visibility === 'private') {
        const updated = await updateRouteMeta(route.id, route.name, route.description, 'link');
        if (!updated) throw new Error('Could not make the route shareable.');
        await putLocalRoute(updated, true).catch(() => undefined);
        if (activeRoute?.id === route.id) setActiveRoute({ ...activeRoute, visibility: 'link' });
      }
      const sent = await shareRoute(route.id, route.name);
      if (!sent) throw new Error('Not connected to a channel.');
      setChannelShared(true); // auto-clears via effect
      refresh();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Sharing failed');
    } finally {
      setBusy(false);
    }
  };

  const toggleFollow = async () => {
    if (!route || busy) return;
    setBusy(true);
    setActionError(null);
    try {
      if (following) {
        await unfollowRoute(route.id);
        await deleteLocalRoute(route.id).catch(() => undefined);
        navigate('/routes');
        return;
      }
      const ok = await followRoute(route.id);
      if (!ok) throw new Error('Sign in to follow routes.');
      refresh();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Follow failed');
    } finally {
      setBusy(false);
    }
  };

  /** "Save a copy" / "Keep as my own": clone into an owned route — a snapshot, not a reference. */
  const saveCopy = async () => {
    if (!route || busy) return;
    setBusy(true);
    setActionError(null);
    try {
      const copy = await saveRoute({
        id: crypto.randomUUID(),
        name: frozen ? route.name : `Copy of ${route.name}`,
        description: route.description,
        source: route.source,
        coordinates: routeCoordinates(),
        durationSeconds: route.durationSeconds,
        plan: route.plan,
      });
      if (!copy) throw new Error('Sign in to save routes.');
      await putLocalRoute(copy, true).catch(() => undefined);
      if (frozen && following) {
        // Keeping a frozen route as your own releases the snapshot (last unfollow GCs it).
        await unfollowRoute(route.id).catch(() => undefined);
        await deleteLocalRoute(route.id).catch(() => undefined);
      }
      navigate(`/routes/${copy.id}`);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Saving a copy failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mg-page">
      <header className="mg-page-header">
        <button className="mg-icon-btn" onClick={() => navigate('/routes')} aria-label="Back to routes" title="Back to routes">
          <ArrowLeft size={26} />
        </button>
        <h1 className="mg-page-title">{route ? route.name : 'Route'}</h1>
        {route && owned && !frozen && !editing && (
          <button className="mg-icon-btn" onClick={beginEdit} aria-label="Rename route" title="Rename route">
            <Pencil size={22} />
          </button>
        )}
      </header>

      {state.status === 'loading' && (
        <div className="mg-page-body">
          <p className="mg-empty">Loading route…</p>
        </div>
      )}

      {state.status === 'notfound' && (
        <div className="mg-page-body">
          <p className="mg-empty">Route not found</p>
          <p className="mg-empty-sub">It may have been deleted, unshared, or you may need to sign in.</p>
        </div>
      )}

      {state.status === 'error' && (
        <div className="mg-page-body">
          <p className="mg-empty">Couldn&rsquo;t load the route</p>
          <p className="mg-empty-sub">{state.error}</p>
          <button
            className="mg-btn mg-btn-outline"
            style={{ width: 'auto', padding: '10px 24px', marginTop: 12 }}
            onClick={refresh}
          >
            Retry
          </button>
        </div>
      )}

      {route && (
        <div className="mg-page-body" style={{ justifyContent: 'flex-start', gap: 16 }}>
          {state.status === 'ready' && state.fromMirror && (
            <p className="mg-empty-sub" style={{ margin: 0 }}>Offline copy — shown from this device&rsquo;s storage</p>
          )}

          {!owned && route.ownerName && (
            <p className="mg-empty-sub" style={{ margin: 0 }}>by {route.ownerName}</p>
          )}

          {frozen && (
            <p className="mg-empty-sub" style={{ margin: 0 }}>
              No longer updated by the owner — this is your frozen copy.
            </p>
          )}

          {/* Wrappers are display:contents until the wide/landscape grid kicks in
              (.mg-route-cols in index.css) — portrait layout is untouched. */}
          <div className="mg-route-cols">
            <div className="mg-route-hero">
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <RideSparkline polyline={route.overviewPolyline} width={192} height={128} />
              </div>
            </div>

            <div className="mg-route-info">
              <div className="mg-review-stats">
                <div className="mg-stat">
                  <span className="mg-stat-value">{formatDistanceMeters(route.distanceMeters)}</span>
                  <span className="mg-stat-label">Distance</span>
                </div>
                <div className="mg-stat">
                  <span className="mg-stat-value">
                    {route.durationSeconds !== null ? formatDurationSeconds(route.durationSeconds) : '—'}
                  </span>
                  <span className="mg-stat-label">Riding time</span>
                </div>
                <div className="mg-stat">
                  <span className="mg-stat-value">{SOURCE_LABELS[route.source]}</span>
                  <span className="mg-stat-label">{formatRideDate(route.createdAt)}</span>
                </div>
              </div>

              {route.description && !editing && <p className="mg-empty-sub" style={{ margin: 0 }}>{route.description}</p>}

              {editing ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <input
                    className="mg-input"
                    value={editName}
                    maxLength={120}
                    onChange={(e) => setEditName(e.target.value)}
                    placeholder="Route name"
                  />
                  <input
                    className="mg-input"
                    value={editDescription}
                    maxLength={2000}
                    onChange={(e) => setEditDescription(e.target.value)}
                    placeholder="Description (optional)"
                  />
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button className="mg-btn mg-btn-primary" onClick={() => void commitEdit()} disabled={busy || !editName.trim()}>
                      <Check size={18} /> Save
                    </button>
                    <button className="mg-btn mg-btn-outline" onClick={() => setEditing(false)} disabled={busy}>
                      <X size={18} /> Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <button className="mg-btn mg-btn-primary" onClick={showOnMap}>
                    <MapIcon size={18} /> Show on map
                  </button>
                  <button className="mg-btn mg-btn-outline" onClick={exportGpx}>
                    <Download size={18} /> Export GPX
                  </button>
                  {owned && !frozen && (
                    <button className="mg-btn mg-btn-outline" onClick={editRoute}>
                      <Waypoints size={18} /> Edit route
                    </button>
                  )}

                  {/* Owner sharing controls */}
                  {owned && !frozen && (
                    <>
                      <div className="mg-setting-row" style={{ minHeight: 0 }}>
                        <span className="mg-setting-label">Sharing</span>
                        <div className="mg-segment">
                          {(['private', 'link', 'public'] as RouteVisibility[]).map((v) => (
                            <button
                              key={v}
                              className={route.visibility === v ? 'mg-segment-btn active' : 'mg-segment-btn'}
                              onClick={() => void setVisibility(v)}
                              disabled={busy}
                            >
                              {v === 'private' ? 'Private' : v === 'link' ? 'Link' : 'Public'}
                            </button>
                          ))}
                        </div>
                      </div>
                      {route.visibility === 'public' && (
                        <p className="mg-empty-sub" style={{ margin: 0 }}>
                          Public — anyone can find this route in Discover.
                        </p>
                      )}
                      {route.visibility !== 'private' && (
                        <button className="mg-btn mg-btn-outline" onClick={() => void copyShareLink()}>
                          <Copy size={18} /> {linkCopied ? 'Link copied' : 'Copy share link'}
                        </button>
                      )}
                      {inChannel && (
                        <button className="mg-btn mg-btn-outline" onClick={() => void sendToChannel()} disabled={busy}>
                          <Radio size={18} />{' '}
                          {channelShared
                            ? 'Sent to channel'
                            : confirmChannelShare
                              ? 'Tap again — this makes the route link-visible'
                              : 'Send to channel'}
                        </button>
                      )}
                    </>
                  )}

                  {/* Non-owner actions: follow / copy (sign-in required) */}
                  {!owned &&
                    (isGuest ? (
                      <button className="mg-btn mg-btn-outline" onClick={exitGuestMode}>
                        <UserPlus size={18} /> Sign in to save this route
                      </button>
                    ) : (
                      <>
                        {!frozen && (
                          <button className="mg-btn mg-btn-outline" onClick={() => void toggleFollow()} disabled={busy}>
                            {following ? <UserMinus size={18} /> : <UserPlus size={18} />}{' '}
                            {following ? 'Unfollow' : 'Follow — keep in my library'}
                          </button>
                        )}
                        <button className="mg-btn mg-btn-outline" onClick={() => void saveCopy()} disabled={busy}>
                          <Link2 size={18} /> {frozen ? 'Keep as my own' : 'Save a copy'}
                        </button>
                      </>
                    ))}

                  {owned && !frozen && (
                    <button className="mg-btn mg-btn-danger" onClick={() => void remove()} disabled={busy}>
                      <Trash2 size={18} /> {confirmDelete ? 'Tap again to delete' : 'Delete route'}
                    </button>
                  )}
                </div>
              )}

              {actionError && <p className="mg-empty-sub" style={{ margin: 0 }}>{actionError}</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RouteDetailPage;
